import axios from '../../axios'
import Axios, { AxiosRequestConfig } from 'axios'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { Project, ProjectType } from './types'
import { PROJECT_TYPE } from 'app/constants'
import { downloadCSV, getDownloadFileName, isProjectInProgress } from 'app/helpers'

export class ProjectsStore {
  loadingList: boolean = false
  deleting: boolean = false
  list: Project[] = []
  entries: {
    [key: string]: Project
  } = {}
  totalPages: number = 0
  totalElements: number = 0
  cancelTokenSource = () => {}

  constructor() {
    makeObservable(this, {
      loadingList: observable,
      deleting: observable,
      list: observable,
      entries: observable,
      totalPages: observable,
      totalElements: observable,
      getData: action,
      delete: action,
      runProject: action,
      setProject: action,
      reset: action,
      clearList: action,
      removeFromList: action,
      setLoadingList: action,
      inProgressProjects: computed,
    })
  }

  get inProgressProjects() {
    return this.list.filter(isProjectInProgress)
  }

  reset = () => {
    this.clearList()
    this.deleting = false
    this.entries = {}
    this.cancelTokenSource = () => {}
  }

  clearList = () => {
    this.loadingList = false
    this.list = []
    this.totalPages = 0
    this.totalElements = 0
  }

  setLoadingList = (value: boolean) => {
    this.loadingList = value
  }

  removeFromList = (id: number) => {
    this.list = this.list.filter((item) => item.id !== id)
  }

  create = async (data: object, autorun: boolean) => {
    return await axios.post(autorun ? '/api/v1/project/create_execute' : '/api/v1/project', data).then((response) => {
      toast.success('Created successfully')
      return response
    })
  }

  update = async (id: number, data: object) => {
    return await axios.put('/api/v1/project/' + id, data).then((response) => {
      toast.success('Updated successfully')
      return response
    })
  }

  delete = async (id: number) => {
    this.deleting = true
    await axios
      .delete('/api/v1/project/' + id)
      .then(({ data }) => {
        toast.success(
          'Delete is started. You will be notified when the process is completed'
        )
        this.setProject(data)
      })
      .finally(() => {
        this.deleting = false
      })
  }

  getById = async (id: number) => {
    return await axios(`/api/v1/project/${id}?withFeaturesLabels=true`).then(
      ({ data }) => {
        this.setProject(data)
        return data
      }
    )
  }

  getData = (filters: any = {}) => {
    if (typeof this.cancelTokenSource == 'function') {
      // this.cancelTokenSource()
    }

    if (filters.withIdsFilter && !filters.ids) {
      this.clearList()
      return
    }

    const cancelTokenSource = Axios.CancelToken.source()

    const params: any = {
      ...filters,
      name: filters.searchValue,
      access_filter: filters.sharingStatus,
    }
    delete params.searchValue
    delete params.ownership
    delete params.sharingStatus

    this.setLoadingList(true)

    const config: AxiosRequestConfig = {
      cancelToken: cancelTokenSource.token,
      params,
    }
    axios
      .get(`/api/v1/project`, config)
      .then(({ data }) => {
        if (data?.content) {
          runInAction(() => {
            this.list = data.content
            this.totalPages = data.totalPages
            this.totalElements = data.totalElements
            this.list.forEach((item) => (this.entries[`${item.id}`] = item))
          })
        }
      })
      .catch(() => {
        this.clearList()
      })
      .finally(() => {
        this.setLoadingList(false)
      })

    this.cancelTokenSource = cancelTokenSource.cancel
  }

  searchByName = async (name: string) => {
    return await axios
      .get(`/api/v1/project`, {
        params: {
          name,
          size: 10,
          type: PROJECT_TYPE.PREDICTION,
        },
      })
      .then(({ data }) => data?.content)
  }

  searchByNameAndType = async (name: string, type: typeof PROJECT_TYPE) => {
    return await axios
      .get(`/api/v1/project`, {
        params: {
          name,
          size: 10,
          type: type,
        },
      })
      .then(({ data }) => data?.content)
  }

  isNameExists = async (name: string, type: ProjectType) => {
    return await axios(
      `/api/v1/project/name/exists?name=${name}&type=${type}`
    ).then(({ data }) => data)
  }

  getProjectFeatureStats = async (id: number, feature: string) => {
    return await axios(`/api/v1/project/${id}/${feature}/stats`).then(
      ({ data }) => data
    )
  }

  runProject = (id: number) => {
    return axios.put('/api/v1/project/' + id + '/execute/').then(({ data }) => {
      toast.success('Running successfully')
      this.setProject(data)
    })
  }
  cancelProject = (id: number) => {
    return axios.put('/api/v1/project/' + id + '/cancel').then(({ data }) => {
      toast.success('Started canceling')
      this.setProject(data)
    })
  }
  setProject = (project: Project) => {
    const index = this.list.findIndex((item: Project) => item.id === project.id)
    if (index !== -1) {
      this.list[index] = project
    }
    this.entries[`${project.id}`] = project
  }

  getLabels = async (id: number) => {
    return await axios(`/api/v1/project/${id}/labels`).then(
      ({ data }) => data.labels
    )
  }

  updateLabels = async (id: number, labels: object) => {
    return await axios
      .put(`/api/v1/project/${id}/labels`, { labels })
      .then((response) => {
        toast.success('Updated successfully')
        return response
      })
  }

  downloadProjectResult = async (id: number, sort?: object, filters?: any) => {
    const body = {
      sort,
      filters,
    }
    await axios
      .post('/api/v1/project/' + id + '/csv', body)
      .then(({ data, headers }) => {
        const blob = new Blob([data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url

        const disposition = headers['content-disposition']
        const matches = /"([^"]*)"/.exec(disposition)
        const filename = matches != null && matches[1] ? matches[1] : 'data.csv'

        link.download = filename
        link.click()
        // window.open(url, '_self')
      })
  }

  downloadAllDocumentsInZipFile = async (id: number) => {
    return await axios('/api/v1/project/' + id + '/documents/zip')
    .then(({ data, headers }) => {
      const link = document.createElement('a')
      link.href = data;
      link.download = 'documents.zip'
      link.click()
    })
  }

  getDatasetDependentProjects = async (datasetId: number) => {
    return await axios(
      `/api/v1/project?size=10000&access_filter=ALL&datasetId=${datasetId}&type=${PROJECT_TYPE.PREDICTION}`
    ).then(({ data }) => data.content)
  }

  getPredictorDependentProjects = async (trainingPredictorIds: number[]) => {
    return await axios(
      `/api/v1/project?size=10000&access_filter=ALL&trainingPredictorIds=${trainingPredictorIds}&type=${PROJECT_TYPE.PREDICTION}`
    ).then(({ data }) => data.content)
  }

  exportResultAsDataset = async (id: number, name: string) => {
    return await axios
      .put(`/api/v1/project/${id}/export`, { datasetName: name })
      .then(({ data }) => data)
  }

  getClusterTreeData = async (projectId: number) => {
    return await axios.get(`/api/v1/project/${projectId}/cluster/charts/tree`).then(({ data }) =>  data)
  }

  getClusterData = async () => {
    let data = await axios.get('/api/v1/project/cluster').then(({ data }) =>  data)
    // return JSON.parse(data);
  return data;
  }

  getEstimatedCost = async (datasetId: number, predictorIds: number) => {
    return await axios(`/api/v1/project/cost?datasetId=${datasetId}&predictorsIds=${predictorIds}`).then(({ data }) => data)
  }

  // Returns the cost of the completed tasks in the operation being executed on the object
  getCostOfCompletedTasks = async (id: number) => {
    return await axios(`/api/v1/project/${id}/current-operation-cost`).then(
      ({ data }) => data
    )
  }

  getDocuments = async (id: number) => {
    return await axios('/api/v1/project/' + id + '/documents').then(
      ({ data }) => data
    )
  }

  // Returns the possible number of clusters based on the configuation of the Cluster project
  getNumberOfClusters = async (dataset_id: number, clustering_algorithm: string, score: string) => {
    return await axios(`/api/v1/project/cluster/number-of-clusters?datasetId=${dataset_id}&clusteringAlgorithm=${clustering_algorithm}&score=${score}`).then(({ data }) => data)
   };

  getClusterFullDatasetUrl = async (projectId: number) => {
    return await axios
      .get(`/api/v1/project/${projectId}/cluster/full_dataset`)
      .then(({ data }) => {
        return data
      })
  }

  getProjectsByIds = async (ids: number[]) => {
    return await axios.get('/api/v1/project', {
      params: {
        ids: ids.join(','),
        size: 1000,
      },
    }).then(
      ({ data }) => data.content as Project[]
    )
  }

  getProjectsByIdsAndType = async (ids: number[], projectType: string) => {
    return await axios.get(`/api/v1/project?type=${projectType}`, {
      params: {
        ids: ids.join(','),
        size: 1000,
      },
    }).then(
      ({ data }) => data.content as Project[]
    )
  }

  refetchListIdsWithType = async (ids: number[], projectType: string) => {
    const exitsIds = ids.filter((id) => this.list.findIndex((item) => item.id === id) !== -1)
    const projects = await this.getProjectsByIdsAndType(exitsIds, projectType)
    projects.forEach((project) => {
      const index =  this.list.findIndex((item) => item.id === project.id)
      runInAction(() => {
        this.list[index] = project
      })
    });
  }

  refetchListIds = async (ids: number[]) => {
    const exitsIds = ids.filter((id) => this.list.findIndex((item) => item.id === id) !== -1)
    const projects = await this.getProjectsByIds(exitsIds)
    projects.forEach((project) => {
      const index =  this.list.findIndex((item) => item.id === project.id)
      runInAction(() => {
        this.list[index] = project
      })
    });
  }

}

export const projectsStore = new ProjectsStore()
